import React, { useState, useEffect } from 'react';
import { PolicyImpactResults } from '../../services/policyImpactService';
import { getBuildingsEnergyData } from '../../services/buildingsEnergyDataService';
import {
  RNG_SHARE_2045_BY_SCENARIO,
  getBuildingsBauPathway,
  getBuildingsBauDebugTable,
  INITIAL_STOCK_ELECTRIFIED_BASE_YEAR,
  BUILDINGS_TURNOVER_RATE,
  ACCELERATED_LOCAL_ADOPTION_SPEEDUP,
  turnoverRateForBauScenario,
  type BuildingsBauScenario,
  type BuildingsBauDebugRow,
  type BuildingsBauPathway,
} from '../../services/buildingsBauPathways';
import {
  BuildingsStockChart,
  BuildingsEnergyChart,
  BuildingsEmissionsChart,
  BuildingsEmissionsStackChart,
  RngImpactChart,
  BuildingsBauComparisonChart,
  type BuildingEnergyComparisonSeries,
} from './BuildingsElectrificationCharts';
import type { BuildingEmissionsTimeSeries } from './PolicyImpactTool';
import type { BuildingBillImpactDetail } from '../../services/buildingsCostModel';
import {
  getDefaultBuildingEndUseAssumptions,
  normalizeResidentialGasEndUse,
  normalizeCommercialGasEndUse,
  type BuildingEndUseAssumptionsInput,
  type ResidentialGasEndUseShares,
  type CommercialGasEndUseShares,
} from '../../services/buildingEndUseAssumptions';
import './BuildingElectrificationPolicy.css';

const KWH_PER_THERM = 29.3;
const HEAT_PUMP_COP_DEFAULT = 3.5; // Efficient unit in mild climate
const HEAT_PUMP_COP_MIN = 2.5;
const HEAT_PUMP_COP_MAX = 4;

interface BuildingElectrificationPolicyProps {
  additionalElectrificationPercent: number;
  results: PolicyImpactResults | null;
  bauElectrifiedShare2045?: number | null;
  /** Residential/commercial turnover at 2045 (0–1): share of that sector’s electrifiable gas converted under BAU. */
  bauElectrifiedShareResidential2045?: number | null;
  bauElectrifiedShareCommercial2045?: number | null;
  currentElectrifiedShare2023?: number | null;
  bauTherms2045?: number | null;
  bauKwh2045?: number | null;
  rngShare2045?: number | null;
  location?: string;
  buildingsBauScenario?: BuildingsBauScenario;
  /** RNG share 0–100 by 2045 (BAU assumption); controlled from this panel, drives model when set */
  buildingsRngPercent?: number;
  onBuildingsRngChange?: (value: number) => void;
  /** Heat pump COP (2.5–4); default 3.5. Controlled from this panel when onBuildingsCopChange is set. */
  buildingsCop?: number;
  onBuildingsCopChange?: (value: number) => void;
  /** Building-only emissions by year (from policy time series); for charts. */
  buildingEmissionsTimeSeries?: BuildingEmissionsTimeSeries | null;
  /** Grid decarbonization year (for BAU comparison chart g/kWh ramp). */
  cleanElectricityYear?: number;
  /** Explicit bill impact diagnostics for electrification interpretation. */
  billImpactDiagnostics?: {
    citywideTotalAnnualBillImpact: number;
    monthlyCitywideTotalBillImpact: number;
    cumulativeBillImpact: number;
    billImpactDetail: BuildingBillImpactDetail | null;
    /** Same as citywide total (legacy). */
    annualBillImpact: number;
    monthlyBillImpact: number;
  } | null;
  /** Explicit price diagnostics for electrification interpretation. */
  priceDiagnostics?: {
    electricityPricePerKwh: number;
    gasPricePerTherm: number;
    gasEquivalentPricePerKwh: number;
    kwhPerThermEquivalent: number;
    priceSource: string;
  } | null;
  breakEvenElectricityPricePerKwh?: number | null;
  /** BAU vs policy therms/kWh from getBuildingEnergyTimeSeries (when baseline cache is warm). */
  buildingEnergyComparisonSeries?: BuildingEnergyComparisonSeries | null;
  /** Policy heat pump target at 2045 (0–100) for stock comparison chart. */
  heatPumpAdoptionPercent2045?: number;
  /** Gas end-use shares (fractions); electrification applies to space + water heating pool. */
  buildingEndUseAssumptions?: BuildingEndUseAssumptionsInput | null;
  onBuildingEndUseAssumptionsChange?: (next: BuildingEndUseAssumptionsInput) => void;
}

const BuildingElectrificationPolicy: React.FC<BuildingElectrificationPolicyProps> = ({
  additionalElectrificationPercent,
  results,
  bauElectrifiedShare2045 = null,
  bauElectrifiedShareResidential2045 = null,
  bauElectrifiedShareCommercial2045 = null,
  currentElectrifiedShare2023 = null,
  bauTherms2045 = null,
  bauKwh2045 = null,
  rngShare2045 = null,
  location,
  buildingsBauScenario = 'current_policy',
  buildingsRngPercent,
  onBuildingsRngChange,
  buildingsCop,
  onBuildingsCopChange,
  buildingEmissionsTimeSeries = null,
  cleanElectricityYear = 2045,
  billImpactDiagnostics = null,
  priceDiagnostics = null,
  breakEvenElectricityPricePerKwh = null,
  buildingEnergyComparisonSeries = null,
  heatPumpAdoptionPercent2045 = 0,
  buildingEndUseAssumptions = null,
  onBuildingEndUseAssumptionsChange,
}) => {
  const [advancedOpen, setAdvancedOpen] = useState(false);
  /** BAU-only reference charts (stack, RNG, scenario comparison) — collapsed by default. */
  const [bauReferenceChartsOpen, setBauReferenceChartsOpen] = useState(false);
  const [debugOpen, setDebugOpen] = useState(false);
  const [debugTable, setDebugTable] = useState<BuildingsBauDebugRow[] | null>(null);
  const [pathway, setPathway] = useState<BuildingsBauPathway | null>(null);
  const [pathwaysByScenario, setPathwaysByScenario] = useState<Record<BuildingsBauScenario, BuildingsBauPathway> | null>(null);

  const effectiveCop = buildingsCop ?? HEAT_PUMP_COP_DEFAULT;
  const kwhPerThermDerived = KWH_PER_THERM / effectiveCop;
  const copControlled = onBuildingsCopChange != null && buildingsCop != null;
  const baseline = results?.baseline;
  const projected = results?.projected;
  const reductions = results?.reductions;
  const heatPumpContribution = results?.contributionsByPolicy?.heatPumpAdoption ?? 0;

  // BAU RNG: use panel control when provided (drives model); else show scenario default from baseline
  const bauRngPercent = buildingsRngPercent ?? (rngShare2045 != null ? rngShare2045 * 100 : RNG_SHARE_2045_BY_SCENARIO[buildingsBauScenario] * 100);
  const rngControlled = onBuildingsRngChange != null && buildingsRngPercent != null;
  const bauThermsDisplay = bauTherms2045 ?? baseline?.naturalGasTherms ?? null;
  const bauKwhDisplay = bauKwh2045 ?? baseline?.electricityKwh ?? null;
  const bauEmissionsMT = baseline != null ? baseline.naturalGasGHG + baseline.electricityGHG : null;

  const isBerkeley = location?.toLowerCase().includes('berkeley') ?? false;

  const endUseMerged = buildingEndUseAssumptions ?? getDefaultBuildingEndUseAssumptions();
  const resEndUse = normalizeResidentialGasEndUse(endUseMerged.residential);
  const comEndUse = normalizeCommercialGasEndUse(endUseMerged.commercial);
  const electrifiableResPct = (resEndUse.spaceHeating + resEndUse.waterHeating) * 100;
  const electrifiableComPct = (comEndUse.spaceHeating + comEndUse.waterHeating) * 100;

  const patchResidentialEndUse = (field: keyof ResidentialGasEndUseShares, pct: number) => {
    if (!onBuildingEndUseAssumptionsChange) return;
    const merged = { ...resEndUse, [field]: Math.max(0, pct) / 100 };
    onBuildingEndUseAssumptionsChange({
      ...endUseMerged,
      residential: normalizeResidentialGasEndUse(merged),
      commercial: comEndUse,
    });
  };

  const patchCommercialEndUse = (field: keyof CommercialGasEndUseShares, pct: number) => {
    if (!onBuildingEndUseAssumptionsChange) return;
    const merged = { ...comEndUse, [field]: Math.max(0, pct) / 100 };
    onBuildingEndUseAssumptionsChange({
      ...endUseMerged,
      residential: resEndUse,
      commercial: normalizeCommercialGasEndUse(merged),
    });
  };

  const endUseControlsDisabled = !onBuildingEndUseAssumptionsChange;

  // Load pathway for charts (current scenario); and all three for BAU comparison chart
  useEffect(() => {
    if (!location || !buildingsBauScenario) {
      setPathway(null);
      setPathwaysByScenario(null);
      return;
    }
    let cancelled = false;
    getBuildingsEnergyData(location)
      .then((data) => {
        if (cancelled) return;
        setPathway(getBuildingsBauPathway(data, buildingsBauScenario));
        setPathwaysByScenario({
          conservative: getBuildingsBauPathway(data, 'conservative'),
          current_policy: getBuildingsBauPathway(data, 'current_policy'),
          accelerated: getBuildingsBauPathway(data, 'accelerated'),
        });
      })
      .catch(() => {
        if (!cancelled) {
          setPathway(null);
          setPathwaysByScenario(null);
        }
      });
    return () => { cancelled = true; };
  }, [location, buildingsBauScenario]);

  useEffect(() => {
    if (!isBerkeley || !location) {
      setDebugTable(null);
      return;
    }
    let cancelled = false;
    getBuildingsEnergyData(location)
      .then((data) => {
        if (cancelled) return;
        const gPerKwh2045 = 0;
        setDebugTable(getBuildingsBauDebugTable(data, { gPerKwh2045 }));
      })
      .catch(() => {
        if (!cancelled) setDebugTable(null);
      });
    return () => { cancelled = true; };
  }, [isBerkeley, location]);

  // With policy: from calculatePolicyImpact
  const remainingNgMT = projected?.naturalGasGHG ?? null;
  const electricityEmissionsMT = projected?.electricityGHG ?? null;
  const totalBuildingEmissionsWithPolicy =
    remainingNgMT != null && electricityEmissionsMT != null ? remainingNgMT + electricityEmissionsMT : null;

  const netGHGReduction = reductions?.totalReduction ?? null;
  const electricityEmissionsIncrease =
    baseline != null && projected != null
      ? Math.max(0, projected.electricityGHG - baseline.electricityGHG)
      : null;
  const citywideAnnualBill =
    billImpactDiagnostics?.citywideTotalAnnualBillImpact ?? billImpactDiagnostics?.annualBillImpact;
  const billImpactDirectionLabel =
    billImpactDiagnostics == null || citywideAnnualBill == null
      ? null
      : citywideAnnualBill > 0
        ? 'Electrification increases bills (citywide total)'
        : citywideAnnualBill < 0
          ? 'Electrification lowers bills (citywide total)'
          : 'Electrification has neutral bill impact (citywide total)';

  return (
    <div className="building-electrification-policy">
      <div className="policy-details">
        {/* A. POLICY DESCRIPTION */}
        <div className="details-section">
          <h4>Policy Description</h4>
          <p>
            This policy accelerates the transition from fossil fuel-based building systems (e.g., natural gas
            furnaces and water heaters) to electric technologies (e.g., heat pumps).
          </p>
          <p>
            Under Business-As-Usual (BAU), building electrification already occurs over time due to equipment
            turnover, state policies, and market trends.
          </p>
          <p>
            This policy represents additional local action that accelerates electrification beyond BAU by
            increasing the share of fossil systems replaced with electric alternatives.
          </p>
          <p>
            As electrification increases:
          </p>
          <ul>
            <li>Natural gas consumption declines</li>
            <li>Electricity demand increases</li>
            <li>Emissions shift from direct combustion to the electricity grid</li>
          </ul>
        </div>

        {/* B. CRITICAL ASSUMPTIONS */}
        <div className="details-section">
          <h4>Critical Assumptions</h4>
          <div className="calculation-results">
            <div className="result-item">
              <span className="result-label">Initial electrified share (base year 2023)</span>
              <span className="result-value">{(INITIAL_STOCK_ELECTRIFIED_BASE_YEAR * 100).toFixed(0)}%</span>
              <div className="assumption-description">
                Starting turnover-stock share for <strong>both</strong> residential and commercial BAU tracks
                (INITIAL_STOCK_ELECTRIFIED_BASE_YEAR). The percentages below are shares of{' '}
                <strong>electrifiable</strong> gas (space + water heating) only—cooking, dryers, and other uses
                are unchanged unless you edit end-use shares.
              </div>
            </div>
            <div className="result-item">
              <span className="result-label">Residential — electrifiable gas electrified by 2045 (BAU)</span>
              <span className="result-value">
                {bauElectrifiedShareResidential2045 != null
                  ? `${(bauElectrifiedShareResidential2045 * 100).toFixed(1)}%`
                  : '—'}
              </span>
              {bauElectrifiedShareResidential2045 == null && (
                <div className="assumption-description">From buildings BAU pathway when available.</div>
              )}
            </div>
            <div className="result-item">
              <span className="result-label">Commercial — electrifiable gas electrified by 2045 (BAU)</span>
              <span className="result-value">
                {bauElectrifiedShareCommercial2045 != null
                  ? `${(bauElectrifiedShareCommercial2045 * 100).toFixed(1)}%`
                  : '—'}
              </span>
              {bauElectrifiedShareCommercial2045 == null && (
                <div className="assumption-description">From buildings BAU pathway when available.</div>
              )}
            </div>
            {bauElectrifiedShare2045 != null && (
              <div className="result-item" style={{ gridColumn: '1 / -1' }}>
                <span className="result-label">All buildings — aggregate electrifiable pool (BAU, 2045)</span>
                <span className="result-value">{(bauElectrifiedShare2045 * 100).toFixed(1)}%</span>
                <div className="assumption-description">
                  Weighted across residential and commercial using each sector’s 2045 BAU gas and default
                  electrifiable (space + water) shares—matches the <strong>Electrified share (BAU)</strong> line in
                  the stock chart at 2045.
                </div>
              </div>
            )}
            <div className="result-item">
              <span className="result-label">Additional Building Electrification Beyond BAU</span>
              <span className="result-value">{additionalElectrificationPercent}%</span>
              <div className="assumption-description">
                Additional share of the <strong>remaining electrifiable</strong> natural gas (space and water)
                converted to electric by 2045, on top of BAU—same interpretation as the policy slider in the model
                (100% converts all remaining electrifiable gas, not cooking/other).
              </div>
            </div>
            <div className="result-item">
              <span className="result-label">Equipment Turnover Rate</span>
              <span className="result-value">
                {(turnoverRateForBauScenario(buildingsBauScenario) * 100).toFixed(1)}%/yr
              </span>
              <div className="assumption-description">
                Base stock turnover is {(BUILDINGS_TURNOVER_RATE * 100).toFixed(0)}%/year
                {buildingsBauScenario === 'accelerated'
                  ? ` (Accelerated BAU uses ${(ACCELERATED_LOCAL_ADOPTION_SPEEDUP * 100 - 100).toFixed(0)}% faster local adoption — see below).`
                  : '.'}
              </div>
            </div>
            {buildingsBauScenario === 'accelerated' && (
              <div className="result-item" style={{ gridColumn: '1 / -1' }}>
                <span className="result-label">
                  Accelerated BAU — local adoption {(ACCELERATED_LOCAL_ADOPTION_SPEEDUP * 100 - 100).toFixed(0)}% faster
                </span>
                <span className="result-value">
                  ×{ACCELERATED_LOCAL_ADOPTION_SPEEDUP.toFixed(2)}
                </span>
                <div className="assumption-description">
                  <strong>Critical assumption:</strong> under the Accelerated buildings BAU pathway, local governments
                  are presumed to encourage equipment adoption{' '}
                  {(ACCELERATED_LOCAL_ADOPTION_SPEEDUP * 100 - 100).toFixed(0)}% faster than the base Accelerated
                  sales/turnover path. This multiplies annual stock turnover by{' '}
                  {ACCELERATED_LOCAL_ADOPTION_SPEEDUP.toFixed(2)} and compresses electric-sales timelines by the same
                  factor (base Accelerated reaches 100% electric sales by 2035 for both sectors; with this speedup the
                  ramp completes ~{(
                    (2035 - 2023) /
                    ACCELERATED_LOCAL_ADOPTION_SPEEDUP
                  ).toFixed(1)}{' '}
                  years after 2023). Cooking and other non–space/water gas end uses remain outside the electrifiable
                  pool unless you edit end-use shares.
                </div>
              </div>
            )}
            <div
              className="result-item"
              style={{ gridColumn: '1 / -1' }}
              title="These assumptions determine how building energy is used. Electrification policies primarily affect heating-related uses."
            >
              <span className="result-label">Natural gas end use (by sector)</span>
              <div className="assumption-description" style={{ marginBottom: 10 }}>
                These assumptions determine how building energy is used. Electrification policies primarily affect
                heating-related uses. Editable percentages auto-normalize to 100% within each sector.
              </div>
              <div className="assumption-description" style={{ marginBottom: 12, fontSize: '0.8125rem', color: '#475569' }}>
                <strong>Commercial</strong> natural gas end-use shares are location-specific: the methodology builds a
                weighted average of business establishments by sector for the selected geography (Census Zip Code Business
                Patterns), maps each sector to a building-activity profile from the Commercial Building Energy Consumption
                Survey (CBEC), and combines those with national CBECS natural gas splits—so the commercial mix reflects the
                local establishment mix. <strong>Residential</strong> defaults use national end use shares from the 2021
                Residential Energy Consumption Survey (RECS) unless you edit them.
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
                <div>
                  <strong style={{ fontSize: '0.875rem' }}>Residential (% of res. gas)</strong>
                  <div style={{ marginTop: 8, fontSize: '0.8125rem' }}>
                    <label className="end-use-pct-row">
                      Space heating
                      <input
                        type="number"
                        min={0}
                        max={100}
                        step={0.5}
                        disabled={endUseControlsDisabled}
                        value={Math.round(resEndUse.spaceHeating * 1000) / 10}
                        onChange={(e) => patchResidentialEndUse('spaceHeating', Number(e.target.value))}
                      />
                      %
                    </label>
                    <label className="end-use-pct-row">
                      Water heating
                      <input
                        type="number"
                        min={0}
                        max={100}
                        step={0.5}
                        disabled={endUseControlsDisabled}
                        value={Math.round(resEndUse.waterHeating * 1000) / 10}
                        onChange={(e) => patchResidentialEndUse('waterHeating', Number(e.target.value))}
                      />
                      %
                    </label>
                    <label className="end-use-pct-row">
                      Cooking
                      <input
                        type="number"
                        min={0}
                        max={100}
                        step={0.5}
                        disabled={endUseControlsDisabled}
                        value={Math.round(resEndUse.cooking * 1000) / 10}
                        onChange={(e) => patchResidentialEndUse('cooking', Number(e.target.value))}
                      />
                      %
                    </label>
                    <label className="end-use-pct-row">
                      Clothes drying
                      <input
                        type="number"
                        min={0}
                        max={100}
                        step={0.5}
                        disabled={endUseControlsDisabled}
                        value={Math.round(resEndUse.clothesDrying * 1000) / 10}
                        onChange={(e) => patchResidentialEndUse('clothesDrying', Number(e.target.value))}
                      />
                      %
                    </label>
                  </div>
                  <div className="assumption-description" style={{ marginTop: 8 }}>
                    Electrifiable (space + water): {electrifiableResPct.toFixed(1)}%
                  </div>
                </div>
                <div>
                  <strong style={{ fontSize: '0.875rem' }}>Commercial (% of com. gas)</strong>
                  <div style={{ marginTop: 8, fontSize: '0.8125rem' }}>
                    <label className="end-use-pct-row">
                      Space heating
                      <input
                        type="number"
                        min={0}
                        max={100}
                        step={0.5}
                        disabled={endUseControlsDisabled}
                        value={Math.round(comEndUse.spaceHeating * 1000) / 10}
                        onChange={(e) => patchCommercialEndUse('spaceHeating', Number(e.target.value))}
                      />
                      %
                    </label>
                    <label className="end-use-pct-row">
                      Water heating
                      <input
                        type="number"
                        min={0}
                        max={100}
                        step={0.5}
                        disabled={endUseControlsDisabled}
                        value={Math.round(comEndUse.waterHeating * 1000) / 10}
                        onChange={(e) => patchCommercialEndUse('waterHeating', Number(e.target.value))}
                      />
                      %
                    </label>
                    <label className="end-use-pct-row">
                      Cooking
                      <input
                        type="number"
                        min={0}
                        max={100}
                        step={0.5}
                        disabled={endUseControlsDisabled}
                        value={Math.round(comEndUse.cooking * 1000) / 10}
                        onChange={(e) => patchCommercialEndUse('cooking', Number(e.target.value))}
                      />
                      %
                    </label>
                    <label className="end-use-pct-row">
                      Other
                      <input
                        type="number"
                        min={0}
                        max={100}
                        step={0.5}
                        disabled={endUseControlsDisabled}
                        value={Math.round(comEndUse.other * 1000) / 10}
                        onChange={(e) => patchCommercialEndUse('other', Number(e.target.value))}
                      />
                      %
                    </label>
                  </div>
                  <div className="assumption-description" style={{ marginTop: 8 }}>
                    Electrifiable (space + water): {electrifiableComPct.toFixed(1)}%
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CHARTS: BAU evolution, energy transition, emissions, RNG */}
        <div className="details-section building-electrification-charts-section">
          <h4>Charts &amp; Figures</h4>
          <p className="building-electrification-charts-intro">
            Charts below compare <strong>BAU</strong> (selected buildings pathway) to your <strong>policy scenario</strong> (efficiency, heat pump
            slider, COP, RNG override, and grid clean year where applicable). Optional <strong>BAU reference</strong> charts (emissions by source,
            RNG, three-scenario comparison) are in the expandable section.
          </p>
          <div className="building-electrification-charts-grid">
            <BuildingsStockChart pathway={pathway} localHeatPumpAdoptionPercent2045={heatPumpAdoptionPercent2045} />
            <BuildingsEnergyChart pathway={pathway} energyComparison={buildingEnergyComparisonSeries} />
            <BuildingsEmissionsChart buildingEmissionsTimeSeries={buildingEmissionsTimeSeries} />
          </div>

          <div className="bau-reference-charts-wrap">
            <button
              type="button"
              className="advanced-toggle bau-reference-charts-toggle"
              onClick={() => setBauReferenceChartsOpen((o) => !o)}
              aria-expanded={bauReferenceChartsOpen}
              id="bau-reference-charts-toggle"
            >
              {bauReferenceChartsOpen ? '−' : '+'} BAU reference charts (optional)
            </button>
            {bauReferenceChartsOpen && (
              <div
                className="bau-reference-charts-content advanced-content"
                role="region"
                aria-labelledby="bau-reference-charts-toggle"
              >
                <p className="building-electrification-charts-intro bau-reference-charts-intro">
                  These figures focus on <strong>business-as-usual</strong> context: emissions mix, renewable natural gas share, and
                  comparison across the three stock BAU scenarios. They do not show your local policy slider outcome.
                </p>
                <div className="building-electrification-charts-grid">
                  <BuildingsEmissionsStackChart buildingEmissionsTimeSeries={buildingEmissionsTimeSeries} />
                  <RngImpactChart pathway={pathway} rngOverridePercent={buildingsRngPercent} />
                  <BuildingsBauComparisonChart
                    pathwaysByScenario={pathwaysByScenario}
                    baseGPerKwh={baseline?.electricityCarbonIntensityGPerKwh ?? 300}
                    cleanElectricityYear={cleanElectricityYear}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* C. ADVANCED ASSUMPTIONS: COP, kWh per therm, RNG */}
        <div className="details-section">
          <button
            type="button"
            className="advanced-toggle"
            onClick={() => setAdvancedOpen(!advancedOpen)}
            aria-expanded={advancedOpen}
          >
            {advancedOpen ? '−' : '+'} Advanced Assumptions
          </button>
          {advancedOpen && (
            <div className="advanced-content">
              <div className="result-item">
                <span className="result-label">Heat Pump Efficiency (COP)</span>
                {copControlled ? (
                  <>
                    <span className="result-value">{effectiveCop.toFixed(1)}</span>
                    <input
                      type="range"
                      min={HEAT_PUMP_COP_MIN}
                      max={HEAT_PUMP_COP_MAX}
                      step={0.1}
                      value={effectiveCop}
                      onChange={(e) => onBuildingsCopChange(Number(e.target.value))}
                      className="advanced-slider"
                      style={{ marginTop: 4, width: '100%', maxWidth: 200 }}
                    />
                    <div className="assumption-description">
                      Range 2.5–4. Default 3.5 = efficient units operating in mild climate.
                    </div>
                  </>
                ) : (
                  <span className="result-value">{effectiveCop.toFixed(1)}</span>
                )}
              </div>
              <div className="result-item">
                <span className="result-label">kWh per therm (heat pump)</span>
                <span className="result-value">{kwhPerThermDerived.toFixed(2)} = 29.3 / COP</span>
              </div>
              <div className="result-item">
                <span className="result-label">Renewable Natural Gas Share (BAU, 2045)</span>
                {rngControlled ? (
                  <>
                    <span className="result-value">{bauRngPercent.toFixed(0)}%</span>
                    <input
                      type="range"
                      min={0}
                      max={100}
                      step={5}
                      value={bauRngPercent}
                      onChange={(e) => onBuildingsRngChange(Number(e.target.value))}
                      className="advanced-slider"
                      style={{ marginTop: 4, width: '100%', maxWidth: 200 }}
                    />
                    <div className="assumption-description">
                      BAU assumption: share of remaining natural gas that is renewable by 2045. The 2045 summary uses this value directly; the time series ramps linearly from the base-year RNG share to this 2045 value.
                    </div>
                  </>
                ) : (
                  <>
                    <span className="result-value">{bauRngPercent.toFixed(0)}%</span>
                    <div className="assumption-description">
                      {buildingsBauScenario === 'conservative' && 'Conservative: 10% RNG by 2045.'}
                      {buildingsBauScenario === 'current_policy' && 'Current policy: 30% RNG by 2045.'}
                      {buildingsBauScenario === 'accelerated' && 'Accelerated: 32% RNG by 2045.'}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* D. CALCULATION RESULTS (same logic as PolicyImpactService; use existing results only) */}
        <div className="details-section">
          <h4>Calculation Results</h4>
          <div className="calculation-results">
            <h5 className="results-subheading">— BAU —</h5>
            <div className="result-item">
              <span className="result-label">BAU Natural Gas (2045 therms)</span>
              <span className="result-value">
                {bauThermsDisplay != null ? bauThermsDisplay.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '—'}
              </span>
              {bauTherms2045 == null && bauThermsDisplay != null && (
                <div className="assumption-description">Baseline (2023) until BAU pathway available.</div>
              )}
            </div>
            <div className="result-item">
              <span className="result-label">BAU Electricity (2045 kWh)</span>
              <span className="result-value">
                {bauKwhDisplay != null ? bauKwhDisplay.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '—'}
              </span>
              {bauKwh2045 == null && bauKwhDisplay != null && (
                <div className="assumption-description">Baseline (2023) until BAU pathway available.</div>
              )}
            </div>
            <div className="result-item">
              <span className="result-label">BAU Emissions (MT CO₂e)</span>
              <span className="result-value">
                {bauEmissionsMT != null ? bauEmissionsMT.toLocaleString(undefined, { maximumFractionDigits: 1 }) : '—'}
              </span>
              {bauTherms2045 == null && (
                <div className="assumption-description">Baseline building emissions (2023).</div>
              )}
            </div>

            <h5 className="results-subheading">— With Policy —</h5>
            <div className="result-item">
              <span className="result-label">Remaining Natural Gas (MT CO₂e)</span>
              <span className="result-value">
                {remainingNgMT != null ? remainingNgMT.toLocaleString(undefined, { maximumFractionDigits: 1 }) : '—'}
              </span>
            </div>
            <div className="result-item">
              <span className="result-label">Electricity Emissions (MT CO₂e)</span>
              <span className="result-value">
                {electricityEmissionsMT != null
                  ? electricityEmissionsMT.toLocaleString(undefined, { maximumFractionDigits: 1 })
                  : '—'}
              </span>
            </div>
            <div className="result-item">
              <span className="result-label">Total Building Emissions (MT CO₂e)</span>
              <span className="result-value">
                {totalBuildingEmissionsWithPolicy != null
                  ? totalBuildingEmissionsWithPolicy.toLocaleString(undefined, { maximumFractionDigits: 1 })
                  : '—'}
              </span>
            </div>

            <h5 className="results-subheading">— Net Impact —</h5>
            <div className="result-item">
              <span className="result-label">Net GHG Reduction (MT CO₂e)</span>
              <span className="result-value">
                {netGHGReduction != null ? netGHGReduction.toLocaleString(undefined, { maximumFractionDigits: 1 }) : '—'}
              </span>
            </div>
            <div className="result-item highlight">
              <span className="result-label">Reduction from Electrification (MT CO₂e)</span>
              <span className="result-value">
                {heatPumpContribution.toLocaleString(undefined, { maximumFractionDigits: 1 })}
              </span>
            </div>
            <div className="result-item">
              <span className="result-label">Increase in Electricity Emissions (MT CO₂e)</span>
              <span className="result-value">
                {electricityEmissionsIncrease != null
                  ? electricityEmissionsIncrease.toLocaleString(undefined, { maximumFractionDigits: 1 })
                  : '—'}
              </span>
            </div>
            {billImpactDiagnostics && (
              <>
                <h5 className="results-subheading">— Bill Impact (Operating Cost) —</h5>
                <div className="result-item highlight">
                  <span className="result-label">{billImpactDirectionLabel}</span>
                  <span className="result-value">
                    Citywide total annual bill impact (2045): $
                    {billImpactDiagnostics.citywideTotalAnnualBillImpact.toLocaleString(undefined, {
                      maximumFractionDigits: 0,
                    })}{' '}
                    / year
                  </span>
                  <div className="assumption-description">
                    Scope: all modeled building gas and electricity (residential + commercial). Positive = higher annual spend under
                    policy; negative = lower.
                  </div>
                </div>
                <div className="result-item">
                  <span className="result-label">Monthly (citywide total)</span>
                  <span className="result-value">
                    $
                    {billImpactDiagnostics.monthlyCitywideTotalBillImpact.toLocaleString(undefined, {
                      maximumFractionDigits: 0,
                    })}{' '}
                    / month
                  </span>
                </div>
                <div className="result-item">
                  <span className="result-label">Cumulative bill change (to 2045)</span>
                  <span className="result-value">
                    ${billImpactDiagnostics.cumulativeBillImpact.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </span>
                </div>
                {billImpactDiagnostics.billImpactDetail && (
                  <>
                    <h5 className="results-subheading">— Per home (2045 methodology) —</h5>
                    <p className="assumption-description" style={{ marginBottom: 8 }}>
                      &quot;Average across all homes&quot; uses residential-attributed bill change (residential gas therms + BAU
                      residential share × kWh delta). &quot;Average for affected homes&quot; divides that same numerator by modeled
                      cumulative electrified residential units vs BAU.
                    </p>
                    {billImpactDiagnostics.billImpactDetail.citywideAverageBillImpactPerHousingUnitAnnual != null && (
                      <div className="result-item">
                        <span className="result-label">Citywide total spread across all housing units</span>
                        <span className="result-value">
                          $
                          {billImpactDiagnostics.billImpactDetail.citywideAverageBillImpactPerHousingUnitAnnual.toLocaleString(
                            undefined,
                            { maximumFractionDigits: 0 },
                          )}
                          / home-year ($
                          {billImpactDiagnostics.billImpactDetail.citywidePerHousingUnit.numeratorAnnual.toLocaleString(undefined, {
                            maximumFractionDigits: 0,
                          })}{' '}
                          ÷{' '}
                          {billImpactDiagnostics.billImpactDetail.citywidePerHousingUnit.denominatorHousingUnits?.toLocaleString() ??
                            '—'}{' '}
                          homes). Includes commercial in numerator — not a true per-household bill.
                        </span>
                      </div>
                    )}
                    {billImpactDiagnostics.billImpactDetail.averageAcrossAllHomesAnnualBillImpact != null && (
                      <div className="result-item">
                        <span className="result-label">Average across all homes</span>
                        <span className="result-value">
                          $
                          {billImpactDiagnostics.billImpactDetail.averageAcrossAllHomesAnnualBillImpact.toLocaleString(undefined, {
                            maximumFractionDigits: 0,
                          })}
                          / home-year ($
                          {billImpactDiagnostics.billImpactDetail.allHomesResidentialAttributed.numeratorAnnual.toLocaleString(
                            undefined,
                            { maximumFractionDigits: 0 },
                          )}{' '}
                          ÷{' '}
                          {billImpactDiagnostics.billImpactDetail.allHomesResidentialAttributed.denominatorHousingUnits?.toLocaleString() ??
                            '—'}{' '}
                          homes); monthly ~$
                          {billImpactDiagnostics.billImpactDetail.averageAcrossAllHomesMonthlyBillImpact?.toLocaleString(undefined, {
                            maximumFractionDigits: 0,
                          }) ?? '—'}
                        </span>
                      </div>
                    )}
                    {billImpactDiagnostics.billImpactDetail.averageForAffectedHomesAnnualBillImpact != null ? (
                      <div className="result-item">
                        <span className="result-label">Average for affected homes</span>
                        <span className="result-value">
                          $
                          {billImpactDiagnostics.billImpactDetail.averageForAffectedHomesAnnualBillImpact.toLocaleString(undefined, {
                            maximumFractionDigits: 0,
                          })}
                          / home-year ($
                          {billImpactDiagnostics.billImpactDetail.affectedHomesResidentialAttributed.numeratorAnnual.toLocaleString(
                            undefined,
                            { maximumFractionDigits: 0 },
                          )}{' '}
                          ÷{' '}
                          {billImpactDiagnostics.billImpactDetail.affectedHomesResidentialAttributed.denominatorElectrifiedHomes.toLocaleString()}{' '}
                          electrified homes); monthly ~$
                          {billImpactDiagnostics.billImpactDetail.averageForAffectedHomesMonthlyBillImpact?.toLocaleString(undefined, {
                            maximumFractionDigits: 0,
                          }) ?? '—'}
                        </span>
                      </div>
                    ) : (
                      <div className="result-item">
                        <span className="result-label">Average for affected homes</span>
                        <span className="result-value">
                          — (0 modeled electrified residential homes in 2045 vs BAU; denominator:{' '}
                          {billImpactDiagnostics.billImpactDetail.cumulativeElectrifiedResidentialHomes2045.toLocaleString()})
                        </span>
                      </div>
                    )}
                    <div className="result-item">
                      <span className="result-label">Energy deltas (2045)</span>
                      <span className="result-value">
                        Therms reduced (BAU − policy):{' '}
                        {Math.round(billImpactDiagnostics.billImpactDetail.annualThermsReduced2045).toLocaleString()} · kWh added
                        (policy − BAU): {Math.round(billImpactDiagnostics.billImpactDetail.annualKwhAdded2045).toLocaleString()} · BAU
                        residential therm share: {(billImpactDiagnostics.billImpactDetail.residentialThermShare2045 * 100).toFixed(1)}%
                      </span>
                    </div>
                  </>
                )}
              </>
            )}
            {priceDiagnostics && (
              <>
                <h5 className="results-subheading">— Comparable Energy Prices —</h5>
                <div className="result-item">
                  <span className="result-label">Electricity price</span>
                  <span className="result-value">${priceDiagnostics.electricityPricePerKwh.toFixed(3)} / kWh</span>
                </div>
                <div className="result-item">
                  <span className="result-label">Natural gas price</span>
                  <span className="result-value">${priceDiagnostics.gasPricePerTherm.toFixed(3)} / therm</span>
                </div>
                <div className="result-item">
                  <span className="result-label">Natural gas electric-equivalent price</span>
                  <span className="result-value">${priceDiagnostics.gasEquivalentPricePerKwh.toFixed(3)} / kWh-e</span>
                </div>
                <div className="result-item">
                  <span className="result-label">Break-even electricity price</span>
                  <span className="result-value">
                    ${((breakEvenElectricityPricePerKwh ?? priceDiagnostics.gasEquivalentPricePerKwh)).toFixed(3)} / kWh
                  </span>
                  <div className="assumption-description">
                    Source: {priceDiagnostics.priceSource}. kWh per therm equivalent = {priceDiagnostics.kwhPerThermEquivalent.toFixed(2)}.
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Berkeley debug table */}
        {isBerkeley && (
          <div className="details-section">
            <button
              type="button"
              className="advanced-toggle"
              onClick={() => setDebugOpen(!debugOpen)}
              aria-expanded={debugOpen}
            >
              {debugOpen ? '−' : '+'} Debug: Berkeley BAU comparison
            </button>
            {debugOpen && debugTable && (
              <div className="advanced-content" style={{ overflowX: 'auto' }}>
                <p className="assumption-description" style={{ marginBottom: 8 }}>
                  Stock electrified share and 2045 outcomes by BAU scenario. Current policy slider 0 = BAU; slider 100 = full local electrification of remaining fossil load (grid clean 2045).
                </p>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                      <th style={{ textAlign: 'left', padding: '6px 8px' }}>Scenario</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px' }}>Stock 2023</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px' }}>Stock 2030</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px' }}>Stock 2045</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px' }}>Therms 2045</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px' }}>kWh 2045</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px' }}>RNG 2045</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px' }}>Bldg MT 2045</th>
                    </tr>
                  </thead>
                  <tbody>
                    {debugTable.map((row, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <td style={{ padding: '6px 8px' }}>
                          {row.scenario === 'current_policy_slider_0' ? 'Current policy (slider 0)' : row.scenario === 'current_policy_slider_100' ? 'Current policy (slider 100)' : row.scenario}
                        </td>
                        <td style={{ textAlign: 'right', padding: '6px 8px' }}>{(row.stockElectrified2023 * 100).toFixed(1)}%</td>
                        <td style={{ textAlign: 'right', padding: '6px 8px' }}>{(row.stockElectrified2030 * 100).toFixed(1)}%</td>
                        <td style={{ textAlign: 'right', padding: '6px 8px' }}>{(row.stockElectrified2045 * 100).toFixed(1)}%</td>
                        <td style={{ textAlign: 'right', padding: '6px 8px' }}>{row.therms2045.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                        <td style={{ textAlign: 'right', padding: '6px 8px' }}>{row.kwh2045.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                        <td style={{ textAlign: 'right', padding: '6px 8px' }}>{(row.rngShare2045 * 100).toFixed(0)}%</td>
                        <td style={{ textAlign: 'right', padding: '6px 8px' }}>{row.buildingEmissions2045MT.toLocaleString(undefined, { maximumFractionDigits: 1 })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {debugOpen && !debugTable && (
              <div className="advanced-content"><p className="assumption-description">Loading…</p></div>
            )}
          </div>
        )}

        {/* E. METHODOLOGY */}
        <div className="details-section">
          <h4>Methodology</h4>
          <p>
            Building emissions are calculated using a stock-based model. Sales electrification
            curves use <strong>linear ramps</strong> to scenario target years (e.g. 100% electric sales by 2035/2045);
            a logistic/S-curve could be added later for smoother adoption.
          </p>
          <p>
            Under BAU, electrification occurs gradually as equipment is replaced over time based on turnover
            rates and appliance sales assumptions. This defines fossil fuel consumption and electricity demand
            over time.
          </p>
          <p>
            The policy increases electrification beyond BAU by converting a fraction of remaining fossil
            systems.
          </p>
          <p>Emissions are calculated as:</p>
          <ul>
            <li>Natural gas emissions (adjusted for renewable natural gas share)</li>
            <li>Electricity emissions (based on grid carbon intensity)</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default BuildingElectrificationPolicy;
